import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppIcons } from "./AppIcons";
import { AppReactOrder, AppReactTally } from "./AppReactRules";

// AppLiveEvents - REACTIONS, EDITS AND "DELETED FOR EVERYONE" IN THE OPEN CHAT WITHIN SECONDS (2026-10-01, owner-picked
// "Live reactions, edits, links"). Before, `/live` carried new MESSAGES only: a reaction, a corrected message and a
// message taken back showed after the next 15-minute rebuild. This file is the page's half of what was added to the
// wire, as rules with no transport and no DOM - the Bridge imports it (the service's writer and the contract tests).
//
// ⭐ THE WIRE (an answer to a chat ask - never the list - may carry, beside `items`):
//   reactions: [{id, to, at, me, emoji, who}]   one reaction ROW: its own id, the message it is ON, when, whose
//                                               (`me` = the owner, from any of their devices), the emoji ("" = taken
//                                               off) and - for the contact's - the name the rebuilt page's tooltip
//                                               would use (`ReactionBook.LabelOf`: their WhatsApp name, else "Them").
//   changes:   [{id, to, at, fromMe, text, revoke?, hide?, hideAll?, original?, needed?, pending?}]
//              (`hide`: deleted on the subscriber's phone - the bubble goes; `hideAll`: the chat was deleted there)
//                                               one EDIT (`text` = the message's words now) or one REVOKE
//                                               (`revoke: true`); `original` / `needed` / `pending` are the
//                                               translation seam's, as on an item.
// ⛔ EVENTS, NOT STATE: each has its own id and time, so the page's cursor (`AppLiveCursor`) takes them exactly like
//    messages - never asked for twice, and the floor moves past them. The page FOLDS them; the service decides nothing
//    about how a pill looks.
// ⛔ AN OLDER PAGE ignores both arrays (it reads `items` and `patches` only); an older service sends neither.
//
// ⭐ THE PILL IS FOLDED WITH THE REBUILD'S RULE (`AppLiveReactRules`, pinned to `ReactionBook` by
//    `LiveReactContract.test.ts`): one reaction per person per message, the newer one wins by TIME (a tie goes to the
//    later arrival), "" withdraws, distinct emoji most used first then the one reacted first, at most three, the count
//    only when more than one person. `/live` is 1:1, so the people are the owner and the contact.
// ⛔ ONLY THE AUTHOR'S OWN CHANGE COUNTS: a change is put on a bubble only when the bubble is on the side the change
//    came from (`AppEditMark.SideOk`) - WhatsApp's protocol does not stop a contact naming one of the owner's messages
//    (`Bridge/src/MessageChanges.ts`), and the service cannot always see the target (it may only be in the phone backup).
// ⛔ READ-ONLY (decision 9): nothing here can reach a sender.

/** One reaction row for the open chat. */
export class AppLiveReaction {
    /** The reaction row's own WhatsApp id - the cursor's key. */
    public Id: string = "";
    /** The message it is on (a bubble's `data-mid`). */
    public To: string = "";
    public At: string = "";
    /** The owner's own reaction (from this app or from their phone). */
    public Me: boolean = false;
    /** The emoji; "" = withdrawn. */
    public Emoji: string = "";
    /** The contact's label for the tooltip ("" for the owner's - the page says "You"). */
    public Who: string = "";
}

/** One edit or revoke for the open chat. */
export class AppLiveChange {
    public Id: string = "";
    /** The message it changes (a bubble's `data-mid`). */
    public To: string = "";
    public At: string = "";
    /** Which side sent the change - it is applied only to a bubble on that side. */
    public FromMe: boolean = false;
    /** "Deleted for everyone". */
    public Revoke: boolean = false;
    /** ⭐ (2026-10-22) Deleted on the subscriber's phone: the bubble `To` goes from the page. */
    public Hide: boolean = false;
    /** ⭐ (2026-10-22) The whole chat was deleted on the phone: every bubble goes. */
    public HideAll: boolean = false;
    /** What the bubble says now (an edit). ⭐ Translation seam: the owner's language when `Needed`. */
    public Text: string = "";
    /** ⭐ Translation seam: what was written, when `Text` is a translation. */
    public Original: string = "";
    public Needed: boolean = false;
    /** ⭐ Its translation is being made: the new words go now, a `patch` follows. */
    public Pending: boolean = false;
}

/** (2026-10-20) One vote's effect: the poll's tally lines now. */
export class AppLivePoll {
    /** The vote row's id - the cursor's key. */
    public Id: string = "";
    /** The poll / event bubble's `data-mid`. */
    public To: string = "";
    public At: string = "";
    public Lines: string[] = [];
}

/** The node members the tally painter touches - a real `Element` has them all. */
export interface IPollNode {
    textContent: string | null;
    className: string;
    firstChild: IPollNode | null;
    getAttribute(Name: string): string | null;
    appendChild(Child: IPollNode): unknown;
    insertBefore(Child: IPollNode, Ref: IPollNode | null): unknown;
    removeChild(Child: IPollNode): unknown;
    querySelector(Selector: string): IPollNode | null;
}

export interface IPollDoc {
    querySelectorAll(Selector: string): ArrayLike<IPollNode>;
    createElement(Tag: string): IPollNode;
}

/**
 * ⭐ (2026-10-20) THE POLL TALLY IN THE OPEN CHAT: the bubble's `ul.wkpoll` (the rebuilt page's and `AppBubbleKinds`'
 * own class) gets the service's lines, by `textContent`; a bubble without one gets it before its time (`.t`). The newest
 * vote per poll wins by time. ⛔ Words only - nothing here is a control (decision 9).
 */
export class AppLivePollPaint {
    private At: Record<string, number> = {};

    /** Answers how many bubbles were redrawn. */
    public Apply = (Doc: IPollDoc, Polls: AppLivePoll[]): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            let Done: number = 0;
            for (const One of Polls) {
                const Ms: number = Date.parse(One.At);
                if (Object.prototype.hasOwnProperty.call(this.At, One.To) === true && this.At[One.To] > Ms) { continue; }
                const Bubbles: ArrayLike<IPollNode> = Doc.querySelectorAll(".b[data-mid]");
                for (let I: number = 0; I < Bubbles.length; I++) {
                    const Bubble: IPollNode = Bubbles[I];
                    if (Bubble.getAttribute("data-mid") !== One.To) { continue; }
                    let List: IPollNode | null = Bubble.querySelector("ul.wkpoll");
                    if (List === null) {
                        List = Doc.createElement("ul");
                        List.className = "wkpoll";
                        Bubble.insertBefore(List, Bubble.querySelector(".t"));
                    }
                    while (List.firstChild !== null) { List.removeChild(List.firstChild); }
                    for (const Line of One.Lines) {
                        const Li: IPollNode = Doc.createElement("li");
                        Li.textContent = Line;
                        List.appendChild(Li);
                    }
                    this.At[One.To] = Ms;
                    Done = Done + 1;
                }
            }
            return Done;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLivePollPaint.Apply");
            return 0;
        }
    };
}

export class AppLiveEvents {
    /** At most this many of each per answer (the service sends the newest). */
    public static readonly Max: number = 100;
    public static readonly MaxEmojiChars: number = 32;
    public static readonly MaxWhoChars: number = 60;
    public static readonly MaxTextChars: number = 32000;
    /** (2026-10-20) A live poll tally: at most this many lines (`AppBubbleKinds.Strings`' 12), each this long. */
    public static readonly MaxPollLines: number = 12;
    public static readonly MaxPollLineChars: number = 200;

    /**
     * ⭐ (2026-10-20) "I TAKE POLL TALLIES": `polls: true` added to an ask body that already says `events: true`.
     * ⛔ The same rule as `events`: an older page sends nothing, so the service sends no `polls` and never answers a
     * held request for a vote. DOCUMENTED EXCEPTION: a JSON round trip; a body that does not parse goes as it was.
     */
    public static WithPolls = (Body: string): string => {
        // ERROR-SUPPRESSED-JUSTIFIED: the body is our own; an unparsable one is sent unchanged (the service refuses it).
        try {
            const Doc: unknown = JSON.parse(Body);
            if (Doc === null || typeof Doc !== "object" || Array.isArray(Doc) === true) { return Body; }
            if ((Doc as Record<string, unknown>).events !== true) { return Body; }
            (Doc as Record<string, unknown>).polls = true;
            return JSON.stringify(Doc);
        } catch {
            return Body;
        }
    };

    /** The answer text's `polls` (`[{id, to, at, lines}]`). ⛔ Each field its own `typeof`; a malformed one is dropped. */
    public static PollsIn = (Text: string): AppLivePoll[] => {
        const Out: AppLivePoll[] = [];
        if (getCoreUtils().KErrorMode === true) { return Out; }
        let Raw: unknown = null;
        // ERROR-SUPPRESSED-JUSTIFIED: an answer that is not JSON carries no polls (the feed's own parse says why).
        try { Raw = (JSON.parse(Text) as Record<string, unknown>).polls; } catch { return Out; }
        try {
            if (Array.isArray(Raw) === false) { return Out; }
            for (const One of Raw as unknown[]) {
                if (One === null || typeof One !== "object" || Array.isArray(One) === true) { continue; }
                const Doc: Record<string, unknown> = One as Record<string, unknown>;
                if (AppLiveEvents.IdOk(Doc.id) === false || AppLiveEvents.IdOk(Doc.to) === false) { continue; }
                if (typeof Doc.at !== "string" || isNaN(Date.parse(Doc.at)) === true) { continue; }
                if (Array.isArray(Doc.lines) === false) { continue; }
                const Lines: string[] = [];
                for (const L of Doc.lines as unknown[]) {
                    if (typeof L === "string" && Lines.length < AppLiveEvents.MaxPollLines) { Lines.push(L.substring(0, AppLiveEvents.MaxPollLineChars)); }
                }
                if (Lines.length === 0) { continue; }
                const Item: AppLivePoll = new AppLivePoll();
                Item.Id = Doc.id as string;
                Item.To = Doc.to as string;
                Item.At = Doc.at;
                Item.Lines = Lines;
                if (Out.length < AppLiveEvents.Max) { Out.push(Item); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEvents.PollsIn");
            return [];
        }
    };
    private static readonly IdPattern: RegExp = /^[A-Za-z0-9_-]{1,64}$/;

    /** An id as the wire may carry it. DOCUMENTED EXCEPTION: pure pattern test. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static IdOk = (Value: unknown): boolean => {
        return typeof Value === "string" && AppLiveEvents.IdPattern.test(Value) === true;
    };

    /**
     * An emoji as a pill can hold it: a short string with no white space (the pill's `data-others` is a
     * space-separated list) - or "" (withdrawn). DOCUMENTED EXCEPTION: pure test. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static EmojiOk = (Value: unknown): boolean => {
        if (typeof Value !== "string" || Value.length > AppLiveEvents.MaxEmojiChars) { return false; }
        return /\s/.test(Value) === false;
    };

    /** The answer's `reactions`. ⛔ Every field with its own `typeof`, never coerced; a malformed one is dropped. */
    public static ReactionsOf = (Raw: unknown): AppLiveReaction[] => {
        const Out: AppLiveReaction[] = [];
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            if (Array.isArray(Raw) === false) { return Out; }
            for (const One of Raw as unknown[]) {
                if (One === null || typeof One !== "object" || Array.isArray(One) === true) { continue; }
                const Doc: Record<string, unknown> = One as Record<string, unknown>;
                if (AppLiveEvents.IdOk(Doc.id) === false || AppLiveEvents.IdOk(Doc.to) === false) { continue; }
                if (typeof Doc.at !== "string" || isNaN(Date.parse(Doc.at)) === true) { continue; }
                if (AppLiveEvents.EmojiOk(Doc.emoji) === false) { continue; }
                const Item: AppLiveReaction = new AppLiveReaction();
                Item.Id = Doc.id as string;
                Item.To = Doc.to as string;
                Item.At = Doc.at;
                Item.Me = Doc.me === true;
                Item.Emoji = (Doc.emoji as string).trim();
                if (typeof Doc.who === "string") { Item.Who = Doc.who.substring(0, AppLiveEvents.MaxWhoChars); }
                if (Out.length < AppLiveEvents.Max) { Out.push(Item); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEvents.ReactionsOf");
            return [];
        }
    };

    /** The answer's `changes`. ⛔ Every field with its own `typeof`; an edit with no words is dropped. */
    public static ChangesOf = (Raw: unknown): AppLiveChange[] => {
        const Out: AppLiveChange[] = [];
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            if (Array.isArray(Raw) === false) { return Out; }
            for (const One of Raw as unknown[]) {
                const Item: AppLiveChange | null = AppLiveEvents.ChangeOf(One);
                if (Item !== null && Out.length < AppLiveEvents.Max) { Out.push(Item); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEvents.ChangesOf");
            return [];
        }
    };

    /** One wire change, or null. */
    private static ChangeOf = (Raw: unknown): AppLiveChange | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const Doc: Record<string, unknown> = Raw as Record<string, unknown>;
            if (AppLiveEvents.IdOk(Doc.id) === false || AppLiveEvents.IdOk(Doc.to) === false) { return null; }
            if (typeof Doc.at !== "string" || isNaN(Date.parse(Doc.at)) === true) { return null; }
            const Out: AppLiveChange = new AppLiveChange();
            Out.Id = Doc.id as string;
            Out.To = Doc.to as string;
            Out.At = Doc.at;
            Out.FromMe = Doc.fromMe === true;
            Out.Revoke = Doc.revoke === true;
            Out.Hide = Doc.hide === true;
            Out.HideAll = Doc.hideAll === true;
            if (Out.Hide === true || Out.HideAll === true) { return Out; }
            if (typeof Doc.text === "string") { Out.Text = Doc.text.substring(0, AppLiveEvents.MaxTextChars); }
            if (typeof Doc.original === "string") { Out.Original = Doc.original.substring(0, AppLiveEvents.MaxTextChars); }
            Out.Needed = Doc.needed === true && Out.Original.length > 0;
            Out.Pending = Doc.pending === true;
            if (Out.Revoke === false && Out.Text.length === 0) { return null; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEvents.ChangeOf");
            return null;
        }
    };
}

/** One person's standing reaction on one message, as the fold holds it. */
export class AppReactSide {
    /** "" = none (never reacted, or withdrew). */
    public Emoji: string = "";
    /** When (ms). A seat read off a rebuilt pill has a small pseudo-time (0 or 1): older than any live event. */
    public At: number = 0;
    /** The tooltip's label - the contact's side only. */
    public Who: string = "";
}

/** The two people of a 1:1 chat on one message. */
export class AppReactState {
    public Mine: AppReactSide = new AppReactSide();
    public Theirs: AppReactSide = new AppReactSide();
    /**
     * The contact's side as the rebuilt page wrote it, when it held MORE than one entry (the same person under a
     * number and a lid, before the mapping was known) - kept as it is until the contact reacts again.
     */
    public OthersRaw: string[] = [];
    public OthersWhoRaw: string = "";
}

/** What the pill shows and carries - `ReactionBadge`'s fields, by the same rule. */
export class AppReactPill {
    public Emoji: string[] = [];
    public Count: number = 0;
    public Mine: string = "";
    public Others: string[] = [];
    public OthersWho: string = "";
    public Title: string = "";
}

/**
 * ⭐ THE FOLD. `Seed` reads a pill the page (or this app) drew; `Apply` takes one event; `Pill` answers what to draw.
 * Pure - `AppLiveReact` is the act.
 */
export class AppLiveReactRules {
    /** WhatsApp shows three distinct emoji at most (`ReactionRules.MaxEmoji`). */
    public static readonly MaxEmoji: number = 3;
    /** The label of a contact whose row carried no name (`ReactionBook.LabelOf`). */
    public static readonly Them: string = "Them";

    /**
     * The state a drawn pill stands for: `data-mine`, `data-others` (space-separated, oldest first),
     * `data-otherswho`, and whether the owner's emoji is the pill's FIRST face (then the owner reacted first).
     */
    public static Seed = (Mine: string | null, Others: string | null, OthersWho: string | null, MineFirst: boolean): AppReactState => {
        const Out: AppReactState = new AppReactState();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.Mine.Emoji = nz(Mine).trim();
            const List: string[] = [];
            for (const One of nz(Others).split(" ")) { if (One.length > 0) { List.push(One); } }
            if (List.length === 1) { Out.Theirs.Emoji = List[0]; Out.Theirs.Who = AppLiveReactRules.NameIn(nz(OthersWho)); }
            if (List.length > 1) { Out.OthersRaw = List; Out.OthersWhoRaw = nz(OthersWho); }
            // Who reacted first is all a drawn pill says about time: the earlier seat gets 0, the later 1.
            Out.Mine.At = 1;
            Out.Theirs.At = 0;
            if (MineFirst === true) { Out.Mine.At = 0; Out.Theirs.At = 1; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReactRules.Seed");
            return new AppReactState();
        }
    };

    /** "Anna (heart)" -> "Anna" (the label `AppIcons.WhoSaid` wrapped); a text with no such tail is kept whole. */
    private static NameIn = (Who: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const At: number = Who.lastIndexOf(" (");
            if (At <= 0 || Who.charAt(Who.length - 1) !== ")") { return Who; }
            return Who.substring(0, At);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReactRules.NameIn");
            return "";
        }
    };

    /**
     * One event onto the state. True = what the pill SHOWS changed (draw). ⛔ THE NEWER ONE WINS BY TIME - an event
     * older than what that person's seat holds is ignored; a tie goes to the later arrival (`ReactionBook.Add`'s
     * rule). An event that says what the seat already says only moves the seat's time (the feed replays what a
     * rebuilt page already shows - nothing is redrawn for it).
     */
    public static Apply = (State: AppReactState, Ev: AppLiveReaction): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const At: number = Date.parse(Ev.At);
            if (isNaN(At) === true) { return false; }
            let Seat: AppReactSide = State.Theirs;
            if (Ev.Me === true) { Seat = State.Mine; }
            if (At < Seat.At) { return false; }
            const Emoji: string = nz(Ev.Emoji).trim();
            let Who: string = "";
            if (Ev.Me === false) { Who = nz(Ev.Who).trim(); }
            if (Ev.Me === false && Who.length === 0) { Who = AppLiveReactRules.Them; }
            const Raw: boolean = Ev.Me === false && State.OthersRaw.length > 0;
            const Same: boolean = Raw === false && Seat.Emoji === Emoji && (Emoji.length === 0 || Seat.Who === Who);
            Seat.Emoji = Emoji;
            Seat.At = At;
            Seat.Who = Who;
            if (Ev.Me === false) { State.OthersRaw = []; State.OthersWhoRaw = ""; }
            return Same === false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReactRules.Apply");
            return false;
        }
    };

    /** What to draw for a state; null = nobody's reaction stands (no pill). */
    public static Pill = (State: AppReactState): AppReactPill | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Out: AppReactPill = new AppReactPill();
            Out.Mine = State.Mine.Emoji;
            Out.Others = State.OthersRaw.slice();
            Out.OthersWho = State.OthersWhoRaw;
            if (State.Theirs.Emoji.length > 0) {
                Out.Others = [State.Theirs.Emoji];
                Out.OthersWho = AppIcons.WhoSaid(State.Theirs.Who, State.Theirs.Emoji);
            }
            Out.Count = Out.Others.length;
            if (Out.Mine.length > 0) { Out.Count = Out.Count + 1; }
            if (Out.Count === 0) { return null; }
            Out.Emoji = AppLiveReactRules.Ranked(State, Out);
            Out.Title = AppLiveReactRules.TitleOf(Out);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReactRules.Pill");
            return null;
        }
    };

    /**
     * Distinct emoji, most used first, then the one reacted first (`ReactionBook.BadgeOf`'s order); at most three.
     * The contact's entries are read before the owner's, as the rebuild reads them (its people are sorted by key,
     * and a number or a lid sorts before `me`), so an exact tie in time draws the contact's first there and here.
     */
    private static Ranked = (State: AppReactState, Pill: AppReactPill): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Rank: AppReactRanker = new AppReactRanker();
            for (let I: number = 0; I < Pill.Others.length; I = I + 1) {
                // Several raw entries were written oldest first: keep that order below any live time.
                let At: number = State.Theirs.At;
                if (State.OthersRaw.length > 1) { At = I / (Pill.Others.length + 1); }
                Rank.Add(Pill.Others[I], At);
            }
            if (Pill.Mine.length > 0) { Rank.Add(Pill.Mine, State.Mine.At); }
            return Rank.Sorted().slice(0, AppLiveReactRules.MaxEmoji);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReactRules.Ranked");
            return [];
        }
    };

    /** "Reactions: Anna (heart), You (thumbs up)" - the contact first, as the rebuilt page's own tooltip reads. */
    public static TitleOf = (Pill: AppReactPill): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Parts: string[] = [];
            if (Pill.OthersWho.length > 0) { Parts.push(Pill.OthersWho); }
            if (Pill.Mine.length > 0) { Parts.push(AppIcons.WhoSaid("You", Pill.Mine)); }
            return "Reactions: " + Parts.join(", ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReactRules.TitleOf");
            return "";
        }
    };
}

/** Counts emoji and orders them most used first, then first reacted. A class, not a closure - house rule. */
class AppReactRanker {
    private Count: Record<string, number> = {};
    private First: Record<string, number> = {};
    private Order: string[] = [];

    /** DOCUMENTED EXCEPTION: adds one value to three tables in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public Add = (Emoji: string, At: number): void => {
        if (this.Count[Emoji] === undefined) { this.Count[Emoji] = 0; this.First[Emoji] = At; this.Order.push(Emoji); }
        this.Count[Emoji] = this.Count[Emoji] + 1;
        if (At < this.First[Emoji]) { this.First[Emoji] = At; }
    };

    public Sorted = (): string[] => AppReactOrder.Sorted(this.Order, this.Compare, "AppReactRanker.Sorted");

    /**
     * DOCUMENTED EXCEPTION: a comparator, called once per pair inside `sort`.
     * ERROR-SUPPRESSED-JUSTIFIED: pure and total over tables built by the caller.
     */
    private Compare = (A: string, B: string): number => {
        return AppReactTally.CompareFirst(this.Count, A, B, this.First);
    };
}

/**
 * ⭐ THE "EDITED" MARK AND WHAT A TAKEN-BACK MESSAGE SAYS - one place for the page writer (`DossierHtml`) and the app
 * (`AppLiveEdit`), pinned by `LiveEditContract.test.ts`.
 *   - an edited bubble carries `data-ed="1"`, and the stylesheet writes "edited" before its clock;
 *   - a message deleted for everyone reads `[deleted]` (what the page has always written for a deleted row).
 * ⛔ NO INLINE STYLE: `Css` rides in the page's own stylesheet when the page has an edited bubble, and ALWAYS in the
 * app's frame sheet (`AppMain.Frame`), so an old page's live edit is marked too.
 */
export class AppEditMark {
    public static readonly Attr: string = "data-ed";
    public static readonly Word: string = "edited";
    public static readonly DeletedText: string = "[deleted]";

    /** ` data-ed="1"` for the page writer, or "". DOCUMENTED EXCEPTION: picks a constant. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public static AttrFor = (Edited: boolean): string => {
        if (Edited !== true) { return ""; }
        return " " + AppEditMark.Attr + "=\"1\"";
    };

    /**
     * Is a bubble with these classes on the side the change came from? `me` = the owner's.
     * DOCUMENTED EXCEPTION: reads one class list in hand. ERROR-SUPPRESSED-JUSTIFIED: pure.
     */
    public static SideOk = (BubbleClass: string | null, FromMe: boolean): boolean => {
        const Mine: boolean = (" " + nz(BubbleClass) + " ").indexOf(" me ") >= 0;
        return Mine === (FromMe === true);
    };

    /**
     * The mark's stylesheet: the word before the clock, and the room the clock's spacer keeps for both (the page
     * reserves 40px for a bare clock - `DossierHtml`'s `.b:has(>.txt+.t)>.txt::after`). The last rules keep that room
     * for a bubble that is ALSO waiting for its translation, and move a translated bubble's flag clear of the
     * longer clock. DOCUMENTED EXCEPTION: a constant stylesheet. ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
     */
    public static Css = (): string => {
        const B: string = ".b[" + AppEditMark.Attr + "]";
        return B + ">.t::before{content:\"" + AppEditMark.Word + "\\00a0 \";font-style:italic}"
            + B + "{min-width:96px}"
            + B + ":has(>.txt+.t)>.txt::after{width:82px}"
            + B + "[data-wac-trwait=\"1\"]:has(>.txt+.t)>.txt::after," + B + "[data-wac-trwait=\"1\"]>.txt::after{width:auto;padding-right:86px}"
            + B + ".tr{min-width:136px}" + B + ".tr .trf{right:86px}";
    };
}
